import { useState } from 'react'
import { apiError } from '../lib/errors'
import { submitOrQueue } from '../lib/offlineSync'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, X, Leaf, Search, ClipboardList, Sparkles, FileUp, QrCode } from 'lucide-react'
import { AIDiagnosisModal } from '../components/shared/AIDiagnosisModal'
import { DocImportModal } from '../components/shared/DocImportModal'
import { PlantModal } from '../components/shared/PlantModal'
import { plantsApi, careApi, blocksApi, recordLink, type Plant, type CareEvent } from '../lib/api'
import { QRLabelSheet, type QRLabelItem } from '../components/shared/QRLabelSheet'
import { varietyColor } from '../lib/varietyColor'

// ── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  greenDark: '#2D6A2F',
  red:       '#F87171',
  blue:      '#60A5FA',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}

// 'dead' = tree died but stays on record (visible). Actual removal is the trash
// button (soft-delete via DELETE). 'removed' kept for legacy rows.
const STATUSES = ['seedling', 'juvenile', 'mature', 'producing', 'declining', 'dead', 'removed']
const ACTIONS   = ['fertilize', 'prune', 'spray', 'irrigate', 'observation', 'graft', 'other']

function statusColor(s: string) {
  if (s === 'producing') return C.green
  if (s === 'removed' || s === 'declining' || s === 'dead') return C.red
  return C.amber
}

function relTime(iso: string) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (m < 2) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}


// ── Quick Care Log ───────────────────────────────────────────────────────────
function QuickCareModal({ plant, onClose }: { plant: Plant; onClose: () => void }) {
  const qc = useQueryClient()
  const [action, setAction] = useState('observation')
  const [notes, setNotes]   = useState('')
  const [cost, setCost]     = useState('')
  const [error, setError]   = useState<string | null>(null)

  const log = useMutation({
    // Offline-capable (§136): queued and synced on reconnect if there's no signal.
    mutationFn: () => submitOrQueue('observation', {
      plant_id: plant.id,
      data: { type: action, notes, observed_at: new Date().toISOString() },
    }, `Care · ${action}${plant.label ? ` · ${plant.label}` : ''}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plants'] })
      qc.invalidateQueries({ queryKey: ['care-events'] })
      onClose()
    },
    onError: (e) => setError(apiError(e, 'Failed to log')),
  })

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 380, maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Log care — {plant.label}</div>
            {plant.variety?.name && <div style={{ fontSize: 11, color: C.textMuted }}>{plant.variety.name}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={16} /></button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Action chips */}
          <div>
            <label style={lbl}>Action</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ACTIONS.map(a => (
                <button key={a} onClick={() => setAction(a)} style={{
                  padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500,
                  background: action === a ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : C.card2,
                  color: action === a ? C.amber : C.textMuted,
                  border: `1px solid ${action === a ? 'color-mix(in srgb, var(--color-primary) 33%, transparent)' : C.border}`,
                  cursor: 'pointer',
                }}>
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 72, fontFamily: 'inherit' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="What did you do? Any observations?" />
          </div>
          <div>
            <label style={lbl}>Cost (USD, optional)</label>
            <input style={{ ...inp, width: 'auto', maxWidth: 120 }} type="number" min={0} step={0.01} value={cost} onChange={e => setCost(e.target.value)} placeholder="0.00" />
          </div>
          {error && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={() => log.mutate()} disabled={log.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.greenDark, border: 'none', color: '#fff', cursor: log.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {log.isPending ? 'Logging…' : 'Log care event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Plant Detail Drawer ──────────────────────────────────────────────────────
function PlantDrawer({ plant, onClose, onEdit, onCare }: {
  plant: Plant; onClose: () => void; onEdit: () => void; onCare: () => void
}) {
  const { data } = useQuery({
    queryKey: ['care-events', plant.id],
    queryFn: () => careApi.events({ plant_id: plant.id, limit: 10 }),
  })
  const events: CareEvent[] = data?.data?.events ?? []

  const actionIcon: Record<string, string> = {
    fertilize: '🌱', prune: '✂️', spray: '💨', irrigate: '💧',
    harvest: '🧺', graft: '🔪', observation: '👁', custom: '📋',
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, display: 'flex' }}>
      <div onClick={onClose} style={{ flex: 1, background: 'rgba(0,0,0,0.5)' }} />
      <div style={{ width: 340, background: C.card, borderLeft: `1px solid ${C.border}`, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 18px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{plant.label}</div>
              {plant.variety?.name && <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>{plant.variety.name}</div>}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint, padding: 4 }}><X size={18} /></button>
          </div>
          {/* Status badge */}
          <div style={{ marginTop: 10 }}>
            <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: statusColor(plant.status) + '22', color: statusColor(plant.status), border: `1px solid ${statusColor(plant.status)}44` }}>
              {plant.status}
            </span>
          </div>
        </div>

        {/* Details */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
          {[
            plant.root_stock && { label: 'Root stock', value: plant.root_stock },
            plant.planted_at && { label: 'Planted', value: new Date(plant.planted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
            plant.height_cm && { label: 'Height', value: `${plant.height_cm} cm` },
            plant.canopy_diameter_cm && { label: 'Canopy', value: `⌀${plant.canopy_diameter_cm} cm` },
          ].filter(Boolean as unknown as (x: unknown) => x is { label: string; value: string }).map((f) => (
            <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: C.textFaint }}>{f.label}</span>
              <span style={{ fontSize: 12, color: C.textMuted }}>{f.value}</span>
            </div>
          ))}
          {plant.notes && (
            <div style={{ marginTop: 8, padding: '10px 12px', background: C.card2, borderRadius: 8, fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>
              {plant.notes}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
          <button onClick={onEdit} style={{ flex: 1, padding: '8px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Pencil size={13} /> Edit
          </button>
          <button onClick={onCare} style={{ flex: 1, padding: '8px 0', borderRadius: 8, background: C.greenDark, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <ClipboardList size={13} /> Log care
          </button>
        </div>

        {/* Care history */}
        <div style={{ padding: '14px 18px', flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Recent care</div>
          {events.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: C.textFaint, fontSize: 13 }}>No care events yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {events.map(ev => (
                <div key={ev.id} style={{ display: 'flex', gap: 10, padding: '8px 10px', background: C.card2, borderRadius: 8 }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{actionIcon[ev.action] ?? '📋'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{ev.action}</div>
                    {ev.notes && <div style={{ fontSize: 11, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.notes}</div>}
                    <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>{relTime(ev.occurred_at)}</div>
                  </div>
                  {ev.cost_usd && <div style={{ fontSize: 11, color: C.textMuted, flexShrink: 0 }}>${ev.cost_usd.toFixed(2)}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export function PlantsPage() {
  const qc = useQueryClient()
  const [modal,     setModal]     = useState<Plant | null | 'new'>(null)
  const [careModal, setCareModal] = useState<Plant | null>(null)
  const [dxModal,   setDxModal]   = useState<Plant | null>(null)
  const [drawer,    setDrawer]    = useState<Plant | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [qrItems, setQrItems] = useState<QRLabelItem[] | null>(null)
  const qrItemFor = (p: Plant): QRLabelItem => ({ url: recordLink('plant', p.id), title: p.label, subtitle: p.variety?.name })
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterBlock, setFilterBlock] = useState('all') // 'all' | 'none' | <blockId>
  const [search,    setSearch]    = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['plants', filterStatus],
    queryFn: () => plantsApi.list(filterStatus !== 'all' ? { status: filterStatus } : undefined).then(r => r.data),
  })
  // Blocks → names so plants can be filtered/labelled by their block.
  const { data: blocksData } = useQuery({ queryKey: ['blocks'], queryFn: () => blocksApi.list().then(r => r.data) })
  const blocks = blocksData?.blocks ?? []
  const blockName = new Map(blocks.map(b => [b.id, b.name]))

  const remove = useMutation({
    mutationFn: (id: string) => plantsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plants'] }),
  })

  const allPlants = data?.plants ?? []
  const byBlock = filterBlock === 'all'
    ? allPlants
    : filterBlock === 'none'
      ? allPlants.filter(p => !p.block_id)
      : allPlants.filter(p => p.block_id === filterBlock)
  const plants = search
    ? byBlock.filter(p =>
        p.label.toLowerCase().includes(search.toLowerCase()) ||
        p.variety?.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.notes?.toLowerCase().includes(search.toLowerCase())
      )
    : byBlock

  // Count by status
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: allPlants.filter(p => p.status === s).length }), {} as Record<string, number>)

  return (
    <main id="main-content" style={{ padding: 32, minHeight: '100dvh', background: C.bg, fontFamily: '"DM Sans", system-ui, sans-serif', color: C.text }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif' }}>Plants</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>
            {allPlants.length} plant{allPlants.length !== 1 ? 's' : ''} tracked · {allPlants.filter(p => p.status === 'producing').length} producing
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {plants.length > 0 && (
            <button onClick={() => setQrItems(plants.map(qrItemFor))} title="Print QR stickers for all listed plants" style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <QrCode size={16} /> QR labels
            </button>
          )}
          <button onClick={() => setImportOpen(true)} title="Automatic import from document/photo" style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9, background: 'color-mix(in srgb, var(--color-primary) 13%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 33%, transparent)`, color: C.amber, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <FileUp size={16} /> Import
          </button>
          <button onClick={() => setModal('new')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.amber, border: 'none', color: C.bg, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> Add plant
          </button>
        </div>
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '0 1 240px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.textFaint }} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search plants…"
            style={{ ...inp, paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {['all', ...STATUSES].map(s => {
            const count = s === 'all' ? allPlants.length : counts[s] ?? 0
            return (
              <button key={s} onClick={() => setFilterStatus(s)} style={{
                padding: '5px 11px', borderRadius: 99, fontSize: 12,
                fontWeight: filterStatus === s ? 600 : 400, cursor: 'pointer',
                background: filterStatus === s ? C.amber : C.card,
                color: filterStatus === s ? C.bg : C.textMuted,
                border: `1px solid ${filterStatus === s ? C.amber : C.border}`,
              }}>
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                {count > 0 && <span style={{ marginLeft: 5, opacity: 0.7 }}>{count}</span>}
              </button>
            )
          })}
        </div>
        {blocks.length > 0 && (
          <select
            value={filterBlock}
            onChange={e => setFilterBlock(e.target.value)}
            title="Filter by block"
            style={{ ...inp, padding: '6px 10px', cursor: 'pointer' }}
          >
            <option value="all">All blocks</option>
            <option value="none">No block</option>
            {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint, fontSize: 14 }}>Loading…</div>
      ) : plants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
          <Leaf size={32} style={{ color: C.textFaint, marginBottom: 12, opacity: 0.5 }} />
          <p style={{ fontSize: 14, color: C.textMuted, margin: 0 }}>
            {search ? `No plants match "${search}"` : 'No plants yet.'}
          </p>
          {!search && (
            <>
              <p style={{ fontSize: 12, color: C.textFaint, margin: '6px 0 16px' }}>Add your first tree or plant to start tracking care and harvest history.</p>
              <button onClick={() => setModal('new')} style={{ padding: '9px 18px', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add first plant</button>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
          {plants.map(p => (
            <div
              key={p.id}
              onClick={() => setDrawer(p)}
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'border-color 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--color-primary) 33%, transparent)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                    <span title={p.variety?.name || 'No variety'} style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: varietyColor(p.variety?.name || p.label) }} />
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</div>
                  </div>
                  {p.variety?.name && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 1, marginLeft: 15 }}>{p.variety.name}</div>}
                  {p.block_id && blockName.has(p.block_id) && (
                    <div style={{ marginTop: 3, marginLeft: 15 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: 'color-mix(in srgb, var(--color-primary) 14%, transparent)', color: C.amber, border: '1px solid color-mix(in srgb, var(--color-primary) 35%, transparent)' }}>
                        ▦ {blockName.get(p.block_id)}
                      </span>
                    </div>
                  )}
                </div>
                <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: statusColor(p.status) + '22', color: statusColor(p.status), border: `1px solid ${statusColor(p.status)}44`, flexShrink: 0 }}>
                  {p.status}
                </span>
              </div>

              {(p.height_cm || p.canopy_diameter_cm || p.root_stock) && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  {p.height_cm && <span style={{ fontSize: 11, color: C.textFaint }}>↕ {p.height_cm}cm</span>}
                  {p.canopy_diameter_cm && <span style={{ fontSize: 11, color: C.textFaint }}>⌀{p.canopy_diameter_cm}cm</span>}
                  {p.root_stock && <span style={{ fontSize: 11, color: C.textFaint }}>{p.root_stock}</span>}
                  {p.planted_at && <span style={{ fontSize: 11, color: C.textFaint }}>{new Date(p.planted_at).getFullYear()}</span>}
                </div>
              )}

              {p.notes && (
                <p style={{ margin: '0 0 8px', fontSize: 12, color: C.textMuted, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>
                  {p.notes}
                </p>
              )}

              {/* Card actions */}
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
                <button
                  onClick={() => setCareModal(p)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 0', borderRadius: 7, background: C.greenDark + '33', border: `1px solid ${C.greenDark}`, color: C.green, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  <ClipboardList size={12} /> Log care
                </button>
                <button
                  title="Automatic photo diagnosis"
                  onClick={() => setDxModal(p)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 10px', borderRadius: 7, background: 'color-mix(in srgb, var(--color-primary) 13%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 33%, transparent)`, color: C.amber, cursor: 'pointer', fontSize: 12 }}>
                  <Sparkles size={12} />
                </button>
                <button
                  title="Print a QR sticker — scan in the field to record picking/activity"
                  onClick={() => setQrItems([qrItemFor(p)])}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 10px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 12 }}>
                  <QrCode size={12} />
                </button>
                <button
                  onClick={() => setModal(p)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 10px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 12 }}>
                  <Pencil size={12} />
                </button>
                <button
                  onClick={() => { if (confirm(`Delete ${p.label}? It will be removed from your records. (To mark a tree dead but keep it, set its status to "dead" instead.)`)) remove.mutate(p.id) }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 10px', borderRadius: 7, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: C.red, cursor: 'pointer' }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {modal !== null && (
        <PlantModal plant={modal === 'new' ? null : modal} onClose={() => setModal(null)} />
      )}
      {careModal && (
        <QuickCareModal plant={careModal} onClose={() => setCareModal(null)} />
      )}
      {dxModal && (
        <AIDiagnosisModal plantId={dxModal.id} plantLabel={`${dxModal.label}${dxModal.variety?.name ? ` (${dxModal.variety.name})` : ''}`} onClose={() => setDxModal(null)} />
      )}
      {importOpen && <DocImportModal onClose={() => setImportOpen(false)} />}
      {qrItems && <QRLabelSheet heading="Plant QR labels" action="Scan to record picking" items={qrItems} onClose={() => setQrItems(null)} />}
      {drawer && (
        <PlantDrawer
          plant={drawer}
          onClose={() => setDrawer(null)}
          onEdit={() => { setModal(drawer); setDrawer(null) }}
          onCare={() => { setCareModal(drawer); setDrawer(null) }}
        />
      )}
    </main>
  )
}
